import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import AdsClickOutlinedIcon from "@mui/icons-material/AdsClickOutlined";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { advanceToolGuideStep } from "../../store/slices/Sidebar/ToolGuideSlice";
import {
  getCurrentStep,
  getStepIndex,
  getToolGuideForRole,
} from "./toolGuideConfig";

const PROGRESS_BAR_COUNT = 3;

const splitActionTokens = (currentAction = "") =>
  String(currentAction)
    .split(" → ")
    .map((token) => token.trim())
    .filter(Boolean);

const isActionTokenMatch = (token, currentStep) => {
  if (!token || !currentStep) return false;
  const normalized = token.toLowerCase();
  const candidates = [
    currentStep.label,
    currentStep.pathLabel,
    ...(currentStep.pathTrail || []),
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);

  if (candidates.includes(normalized)) return true;

  const tokenParts = normalized
    .split("/")
    .map((part) => part.trim())
    .filter(Boolean);
  if (tokenParts.some((part) => candidates.includes(part))) return true;
  if (candidates.some((candidate) => tokenParts.includes(candidate))) return true;

  return candidates.some(
    (candidate) =>
      normalized.includes(candidate) || candidate.includes(normalized)
  );
};

/**
 * Tool Guide card — same colors as the original Flow popup, placed inline
 * in the sidebar under the Tool Guide nav item (not a floating overlay).
 */
const ToolGuidePanel = () => {
  const dispatch = useDispatch();
  const { isPanelOpen, path, bucketName, roleKey, activeStepId } = useSelector(
    (state) => state.toolGuide
  );

  const guide = useMemo(
    () => getToolGuideForRole(roleKey, bucketName),
    [roleKey, bucketName]
  );

  const currentStep = useMemo(
    () => getCurrentStep(guide, activeStepId),
    [guide, activeStepId]
  );

  const steps = guide?.steps || [];
  const stepIndex = getStepIndex(guide, activeStepId);
  const isOnLastStep = steps.length > 0 && stepIndex === steps.length - 1;

  const filledBars = useMemo(() => {
    if (!steps.length) return 1;
    const position = stepIndex < 0 ? 0 : stepIndex;
    return Math.max(
      1,
      Math.min(
        PROGRESS_BAR_COUNT,
        Math.ceil(((position + 1) / steps.length) * PROGRESS_BAR_COUNT)
      )
    );
  }, [steps.length, stepIndex]);

  if (!isPanelOpen || !bucketName) return null;

  const segments =
    path?.length > 0
      ? path.filter(Boolean)
      : guide?.pathBase?.length
        ? [...guide.pathBase]
        : ["My Requests"];

  const parents = segments.length > 1 ? segments.slice(0, -1) : [];
  const currentLabel =
    segments[segments.length - 1] ||
    currentStep?.label ||
    guide?.title ||
    "Tool Guide";

  const actionTokens = splitActionTokens(guide?.currentAction);
  const matchedActionIndex = currentStep
    ? actionTokens.findIndex((token) => isActionTokenMatch(token, currentStep))
    : -1;
  const actionDetail =
    currentStep?.detail ||
    guide?.currentAction ||
    "Open a bucket or perform an action to update the flow.";

  const handleNextStep = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dispatch(advanceToolGuideStep());
  };

  return (
    <div
      className="mt-1 w-full min-w-0 rounded-xl overflow-hidden border border-[#e5e7eb] bg-white shadow-sm animate-tool-guide-notice"
      role="region"
      aria-label="Tool guide"
    >
      <div className="bg-blue-600 px-3 py-2">
        <span className="text-[13px] font-semibold text-white tracking-wide">
          Tool guide
        </span>
      </div>

      {/* Flow path: My Requests / Bucket / … / current action (bold) */}
      <div className="px-3 pt-3 pb-2.5 bg-white text-[12px] leading-snug break-words">
        {parents.map((segment, index) => (
          <React.Fragment key={`${segment}-${index}`}>
            <span className="font-normal text-[#6b7280]">{segment}</span>
            <span className="font-normal text-[#6b7280]"> / </span>
          </React.Fragment>
        ))}
        <span className="font-bold text-[#002d5b]">{currentLabel}</span>
      </div>

      <div className="border-t border-[#e8eaed]" />

      {/* Current action detail */}
      <div className="px-3 py-3 bg-white flex items-start gap-2.5">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#e8f1ff] flex items-center justify-center">
          <AdsClickOutlinedIcon
            sx={{ fontSize: 16, color: "#3b7ddd" }}
            aria-hidden="true"
          />
        </div>
        <p className="min-w-0 pt-1 text-[11px] text-[#6b7280] leading-snug break-words">
          {actionTokens.length > 0 ? (
            <>
              {actionTokens.map((token, index) => (
                <React.Fragment key={`${token}-${index}`}>
                  <span
                    className={
                      index === matchedActionIndex
                        ? "font-bold text-[#002d5b]"
                        : "font-normal text-[#6b7280]"
                    }
                  >
                    {token}
                  </span>
                  {index < actionTokens.length - 1 ? " → " : ""}
                </React.Fragment>
              ))}
            </>
          ) : (
            actionDetail
          )}
        </p>
      </div>

      {steps.length > 0 && (
        <>
          <div className="border-t border-[#e8eaed]" />
          <div className="px-3 py-2 bg-white flex items-center justify-between gap-2">
            <div
              className="flex items-center gap-1"
              aria-hidden="true"
            >
              {Array.from({ length: PROGRESS_BAR_COUNT }).map((_, index) => (
                <span
                  key={index}
                  className={`h-[3px] w-3.5 rounded-full ${
                    index < filledBars ? "bg-blue-600" : "bg-[#d1d5db]"
                  }`}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={handleNextStep}
              className="flex items-center gap-0.5 text-[11px] font-medium text-[#6b7280] hover:text-blue-600 transition-colors duration-150"
              aria-label={
                isOnLastStep
                  ? "Return to flow overview"
                  : "Show the next step in this flow"
              }
            >
              <span>Next step</span>
              <KeyboardArrowRightIcon sx={{ fontSize: 14 }} aria-hidden="true" />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ToolGuidePanel;
